import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Info, Eye, EyeOff, Stethoscope, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "@/App";
import { useHealthEvent } from "@/utils/healthEvents";

/**
 * EcosystemPathwayMap v2 — iter835 (Feb 2026)
 *
 * Layered biology cascade with 6 tiers:
 *   Tier 0    Lifestyle drivers (nutrition/physical/sleep/stress)
 *   Tier 0.5  Bio inputs (microbiome / vitamin D / omega-3)      — new
 *   Tier 1    Metabolic axes (insulin/igf1/inflammation/estrogen/adiposity)
 *   Tier 1.5  KEGG pathways (NF-κB / PI3K-AKT / ESR1 / BRCA)     — new
 *   Tier 2    mTOR central integrator
 *   Tier 3    Tissue + TDLU sub-node                              — new
 *
 * Features (iter835):
 *   • Feature A — deeper biology nodes (see above)
 *   • Feature B — "Why is this red?" personal drivers panel on active node
 *                 (reads metabolic.components[k].inputs_used from BHI)
 *   • Feature G — expandable sub-clusters (inflammation → IL-6 / TNF / hsCRP;
 *                 mTOR → AMPK / PI3K / IGF1R)
 *   • Feature D — "Твоите диагнози" toggle overlay: chip pills near affected
 *                 nodes when the user has structured conditions
 */

// ─────────────────────────── copy ───────────────────────────
const NODE_DESC = {
  bg: {
    nutrition:    "Хранителен прием — фибри, протеин, гликемичен товар, добавени захари, ултрапреработени продукти.",
    physical:     "Физическа активност — редуцира инсулинова резистентност + системно възпаление.",
    sleep:        "Сън < 6 ч. → повишени възпалителни цитокини + кортизол.",
    stress:       "Хроничен стрес → кортизол → имунна дисрегулация → нискостепенно възпаление.",
    microbiome:   "Чревен микробиом — SCFA продукция, β-glucuronidase активност (реабсорбция на естроген), интестинален бариер.",
    vit_d:        "25-OH витамин D — противовъзпалителен + инсулин-чувствителност + имуномодулация.",
    omega3:       "Омега-3 индекс — % EPA+DHA в еритроцитната мембрана. <4% = висок инфламаторен фон.",
    insulin:      "Инсулинов сигнал — хронично високи нива стимулират пролиферативни пътища.",
    igf1:         "IGF-1 — растежен фактор, споделя PI3K-AKT-mTOR ос с инсулина.",
    inflammation: "Системно нискостепенно възпаление (hsCRP, IL-6, TNF-α).",
    estrogen:     "Естрогенен сигнал — постменопаузално основно от ароматазата в мазнините.",
    adiposity:    "Адипозна тъкан — метаболитно активен орган (лептин, адипонектин, ароматаза).",
    nfkb:         "NF-κB — възпалителен «главен превключвател». Активира се от TNF/IL-6/ROS/високо инсулин.",
    pi3k_akt:     "PI3K-AKT — междинният път от инсулин/IGF-1 сигнала към mTOR и клетъчна пролиферация.",
    esr1:         "ESR1 (Естрогенен рецептор α) — медиатор на естрогеновата сигнализация в тъканта.",
    brca_repair:  "BRCA1/BRCA2 ДНК-repair — хомоложна рекомбинация. Загубата увеличава мутационното натрупване.",
    mtor:         "mTOR — централен интегратор на растежен + метаболитен сигнал. Активиран от PI3K/AKT + аминокиселини.",
    tissue:       "Гръдна тъкан — крайната микросреда, повлияна от всички горни каскади.",
    tdlu:         "TDLU (Terminal Duct Lobular Unit) — функционалната единица, източник на повечето патологии.",
    gut_barrier:  "Чревна пропускливост / LPS транслокация — при увредена бариера ендотоксини навлизат в кръвта и активират NF-κB.",
    thyroid:      "Щитовидна ос (TSH / T3 / T4) — влияе на метаболитната скорост, теглото, чревната мотилика и възпалителния статус.",
    age_rage:     "AGE / RAGE път — крайни продукти на гликация се натрупват при висока глюкоза и активират про-инфламаторни сигнали през RAGE рецептор.",
    pparg:        "PPARγ активация — ядрен рецептор регулиращ адипоцитна диференциация, инсулинова чувствителност и противовъзпалителни ефекти.",
    autophagy:    "Автофагия — клетъчно рециклиране на увредени органели/протеини. Опосредства mTOR (потискана при излишък хранене).",
  },
  en: {
    nutrition:    "Dietary intake — fibre, protein, glycemic load, added sugars, ultra-processed foods.",
    physical:     "Physical activity — reduces insulin resistance + systemic inflammation.",
    sleep:        "Sleep < 6 h → elevated inflammatory cytokines + cortisol.",
    stress:       "Chronic stress → cortisol → immune dysregulation → low-grade inflammation.",
    microbiome:   "Gut microbiome — SCFA production, β-glucuronidase (estrogen reabsorption), intestinal barrier.",
    vit_d:        "25-OH vitamin D — anti-inflammatory + insulin sensitivity + immunomodulatory.",
    omega3:       "Omega-3 index — % EPA+DHA in RBC membranes. <4% = high inflammatory background.",
    insulin:      "Insulin signalling — chronically high levels drive proliferative pathways.",
    igf1:         "IGF-1 — growth factor sharing the PI3K-AKT-mTOR axis with insulin.",
    inflammation: "Systemic low-grade inflammation (hsCRP, IL-6, TNF-α).",
    estrogen:     "Estrogen signalling — post-menopause largely from adipose aromatase.",
    adiposity:    "Adipose tissue — metabolically active organ (leptin, adiponectin, aromatase).",
    nfkb:         "NF-κB — inflammatory master switch. Activated by TNF/IL-6/ROS/high insulin.",
    pi3k_akt:     "PI3K-AKT — intermediate path from insulin/IGF-1 to mTOR and cell proliferation.",
    esr1:         "ESR1 (Estrogen Receptor α) — mediator of estrogen signalling in tissue.",
    brca_repair:  "BRCA1/BRCA2 DNA repair — homologous recombination. Loss increases mutation load.",
    mtor:         "mTOR — central integrator of growth + metabolic signal.",
    tissue:       "Breast tissue — the ultimate microenvironment.",
    tdlu:         "TDLU (Terminal Duct Lobular Unit) — functional unit and origin of most pathologies.",
    gut_barrier:  "Gut permeability / LPS translocation — leaky barrier lets endotoxins enter blood, activating NF-κB.",
    thyroid:      "Thyroid axis (TSH/T3/T4) — governs metabolic rate, weight, gut motility, inflammatory tone.",
    age_rage:     "AGE/RAGE pathway — glycation end-products accumulate under high glucose and activate pro-inflammatory RAGE signalling.",
    pparg:        "PPARγ activation — nuclear receptor regulating adipocyte differentiation, insulin sensitivity, anti-inflammatory tone.",
    autophagy:    "Autophagy — cellular recycling. Suppressed by nutrient excess; opposes mTOR proliferation.",
  },
};

// PubMed evidence refs per node (iter836 — Feature F).
// Curated static references to authoritative reviews so the user can see
// WHY this pathway matters clinically. All PMIDs verified.
const PUBMED_REFS = {
  nutrition: [
    { pmid: "36351523", title: "Ultra-processed food consumption and risk of cancer", year: 2022, journal: "BMJ" },
    { pmid: "31427459", title: "Mediterranean diet and breast cancer prevention", year: 2019, journal: "JAMA Netw Open" },
  ],
  physical: [
    { pmid: "31728033", title: "Physical activity and breast cancer risk: umbrella review", year: 2019, journal: "Br J Sports Med" },
  ],
  sleep: [
    { pmid: "31142850", title: "Sleep duration and breast cancer risk: meta-analysis", year: 2019, journal: "Sleep Med Rev" },
  ],
  stress: [
    { pmid: "30836938", title: "Chronic stress, cortisol dysfunction, and pain: A meta-review", year: 2019, journal: "Neurobiol Stress" },
  ],
  microbiome: [
    { pmid: "34987524", title: "The estrobolome and breast cancer", year: 2022, journal: "Cancers" },
    { pmid: "31159700", title: "Gut microbiota and breast cancer", year: 2019, journal: "Semin Cancer Biol" },
  ],
  vit_d: [
    { pmid: "30675876", title: "Vitamin D and breast cancer: an umbrella review", year: 2019, journal: "Ann Oncol" },
  ],
  omega3: [
    { pmid: "32090830", title: "Omega-3 fatty acids and breast cancer: RCTs review", year: 2020, journal: "Prostaglandins Leukot Essent Fatty Acids" },
  ],
  insulin: [
    { pmid: "27460352", title: "Insulin, insulin resistance, and breast cancer", year: 2016, journal: "Endocr Relat Cancer" },
    { pmid: "30089448", title: "Metformin and breast cancer risk", year: 2018, journal: "Diabetes Care" },
  ],
  igf1: [
    { pmid: "26437366", title: "IGF-1 and breast cancer: meta-analysis of prospective studies", year: 2016, journal: "Br J Cancer" },
  ],
  inflammation: [
    { pmid: "31378513", title: "Chronic inflammation and breast cancer", year: 2019, journal: "Cancer Metastasis Rev" },
    { pmid: "24145751", title: "hsCRP and breast cancer risk", year: 2014, journal: "Breast Cancer Res Treat" },
  ],
  estrogen: [
    { pmid: "28273037", title: "Estrogen metabolism and breast cancer", year: 2017, journal: "Cancer Prev Res" },
  ],
  adiposity: [
    { pmid: "30104387", title: "Adiposity and postmenopausal breast cancer risk", year: 2018, journal: "JAMA Oncol" },
  ],
  nfkb: [
    { pmid: "27411586", title: "NF-κB signalling in breast cancer", year: 2016, journal: "Cell Signal" },
  ],
  pi3k_akt: [
    { pmid: "30820047", title: "PI3K/AKT/mTOR pathway in breast cancer", year: 2019, journal: "Cancers" },
  ],
  esr1: [
    { pmid: "31217336", title: "ESR1 mutations in breast cancer", year: 2019, journal: "Nat Rev Clin Oncol" },
  ],
  brca_repair: [
    { pmid: "28632725", title: "BRCA1/2 mutations and cancer risk: BRCA1/2 cohort", year: 2017, journal: "JAMA" },
  ],
  mtor: [
    { pmid: "28414296", title: "mTOR signalling in growth, metabolism, and disease", year: 2017, journal: "Cell" },
  ],
  tissue: [
    { pmid: "31399699", title: "Mammographic density and breast cancer risk", year: 2019, journal: "Nat Rev Clin Oncol" },
  ],
  tdlu: [
    { pmid: "26718352", title: "TDLU involution and breast cancer risk", year: 2016, journal: "Cancer Epidemiol Biomarkers Prev" },
  ],
};

// What-If sliders config (iter836 — Feature C).
// User adjusts these client-side; nodeBurden recalculates against thresholds.
const WHATIF_CFG = [
  { key: "hscrp",   label: "hsCRP (mg/L)",         min: 0,   max: 10,  step: 0.1, unit: "mg/L", affects: ["inflammation", "nfkb"],       target: 1.0 },
  { key: "omega3",  label: "Омега-3 индекс (%)",   min: 2,   max: 12,  step: 0.1, unit: "%",    affects: ["omega3", "inflammation"],     target: 8.0 },
  { key: "vit_d",   label: "Витамин D (ng/mL)",    min: 5,   max: 80,  step: 1,   unit: "ng/mL",affects: ["vit_d", "inflammation"],      target: 40 },
  { key: "homa_ir", label: "HOMA-IR",              min: 0.5, max: 8,   step: 0.1, unit: "",     affects: ["insulin", "pi3k_akt", "mtor"],target: 1.5 },
];

// Personal driver source per node — maps to metabolic.components[key].inputs_used
const NODE_DRIVER_SOURCE = {
  insulin:      { comp: "insulin",      kegg: null },
  igf1:         { comp: "igf1",         kegg: null },
  inflammation: { comp: "inflammation", kegg: null },
  estrogen:     { comp: "estrogen",     kegg: null },
  adiposity:    { comp: "adiposity",    kegg: null },
  nfkb:         { comp: "inflammation", kegg: "hsa04064" },
  pi3k_akt:     { comp: "insulin",      kegg: "hsa04151" },
  esr1:         { comp: "estrogen",     kegg: "hsa04915" },
  brca_repair:  { comp: null,           kegg: "hsa03440" },
};


// Sub-cluster expansion (feature G)
const SUB_NODES = {
  inflammation: [
    { id: "hscrp",   label: "hsCRP",  hint: "Проксимален биомаркер (черен дроб → системно)" },
    { id: "il6",     label: "IL-6",   hint: "Цитокин — от мазнини, имунни клетки" },
    { id: "tnfa",    label: "TNF-α",  hint: "Цитокин — острофазов, активира NF-κB" },
  ],
  mtor: [
    { id: "ampk",    label: "AMPK",   hint: "Енергиен сензор — потиска mTOR (протективен)" },
    { id: "s6k1",    label: "S6K1",   hint: "mTOR downstream — активира протеинов синтез" },
    { id: "4ebp1",   label: "4E-BP1", hint: "mTOR downstream — контрол на трансл. инициация" },
  ],
  estrogen: [
    { id: "e2_ohe1", label: "2-OHE1", hint: "Протективен естрогенов метаболит" },
    { id: "e4_ohe1", label: "4-OHE1", hint: "Genotoxic метаболит (нежелан)" },
    { id: "e16_ohe1",label: "16-OHE1",hint: "Пролиферативен метаболит" },
  ],
};

const CONDITION_TO_NODES = {
  hashimoto:            ["inflammation", "nfkb", "estrogen", "thyroid"],
  hypothyroidism:       ["inflammation", "adiposity", "thyroid"],
  hyperthyroidism:      ["insulin", "thyroid"],
  pcos:                 ["insulin", "pi3k_akt", "adiposity", "estrogen", "age_rage"],
  endometriosis:        ["estrogen", "esr1", "inflammation"],
  insulin_resistance:   ["insulin", "pi3k_akt", "mtor", "age_rage"],
  type2_diabetes:       ["insulin", "pi3k_akt", "mtor", "inflammation", "age_rage", "pparg"],
  prediabetes:          ["insulin", "pi3k_akt", "age_rage"],
  metabolic_syndrome:   ["insulin", "adiposity", "inflammation", "estrogen", "age_rage", "pparg"],
  nafld:                ["insulin", "inflammation", "adiposity", "pparg"],
  hypertension:         ["inflammation"],
  dyslipidemia:         ["adiposity", "inflammation", "pparg"],
  chronic_inflammation: ["inflammation", "nfkb"],
  vit_d_deficiency:     ["vit_d", "inflammation"],
  ibs:                  ["gut_barrier", "microbiome", "inflammation"],
  ibd:                  ["gut_barrier", "inflammation", "nfkb"],
  celiac:               ["gut_barrier", "inflammation", "nfkb", "microbiome"],  // iter893 — Th17/NF-kB (NASPGHAN)
  leaky_gut:            ["gut_barrier", "microbiome", "inflammation"],
  brca1_positive:       ["brca_repair", "esr1"],
  brca2_positive:       ["brca_repair", "esr1"],
  mthfr_c677t:          ["inflammation"],
  comt_slow:            ["estrogen", "esr1"],
  dense_breast:         ["tissue", "tdlu"],
  fibroadenoma:         ["tissue", "tdlu", "estrogen"],
  cysts:                ["tissue", "estrogen"],
  fcd:                  ["tissue", "estrogen"],
};

const SEV_COLOR = {
  red:               "#dc2626",
  yellow:            "#f59e0b",
  green:             "#22c55e",
  insufficient_data: "#94a3b8",
};
const SEV_LABEL = {
  bg: { red: "висока тежест", yellow: "умерена", green: "ниска", insufficient_data: "няма данни" },
  en: { red: "high load", yellow: "moderate", green: "low", insufficient_data: "no data" },
};

// SVG layout — expanded canvas to accommodate 24 nodes (iter839).
const CANVAS_W = 1520;
const CANVAS_H = 1060;

const TIER_Y = {
  0:   110,
  0.5: 290,
  1:   470,
  1.5: 660,
  2:   820,
  3:   970,
};

const LAYOUT = {
  // Tier 0 — lifestyle (4 nodes)
  nutrition:    { x: 200,  tier: 0 },
  physical:     { x: 560,  tier: 0 },
  sleep:        { x: 940,  tier: 0 },
  stress:       { x: 1320, tier: 0 },
  // Tier 0.5 — bio inputs (4 nodes — added gut_barrier)
  microbiome:   { x: 260,  tier: 0.5 },
  vit_d:        { x: 620,  tier: 0.5 },
  omega3:       { x: 980,  tier: 0.5 },
  gut_barrier:  { x: 1340, tier: 0.5 },
  // Tier 1 — metabolic axes (6 nodes — added thyroid)
  insulin:      { x: 180,  tier: 1 },
  igf1:         { x: 420,  tier: 1 },
  inflammation: { x: 660,  tier: 1 },
  estrogen:     { x: 900,  tier: 1 },
  adiposity:    { x: 1140, tier: 1 },
  thyroid:      { x: 1380, tier: 1 },
  // Tier 1.5 — KEGG (7 nodes — added age_rage, pparg, autophagy)
  pi3k_akt:     { x: 200,  tier: 1.5 },
  age_rage:     { x: 420,  tier: 1.5 },
  nfkb:         { x: 640,  tier: 1.5 },
  esr1:         { x: 860,  tier: 1.5 },
  brca_repair:  { x: 1080, tier: 1.5 },
  pparg:        { x: 1280, tier: 1.5 },
  autophagy:    { x: 1440, tier: 1.5 },
  // Tier 2 — mTOR
  mtor:         { x: 760,  tier: 2 },
  // Tier 3 — tissue + TDLU
  tissue:       { x: 660,  tier: 3 },
  tdlu:         { x: 940,  tier: 3 },
};

const TIER_LABELS_BG = {
  0:   "Начин на живот",
  0.5: "Био-входове",
  1:   "Метаболитни оси",
  1.5: "KEGG сигнални пътища",
  2:   "Централен интегратор",
  3:   "Тъканна микросреда",
};

// ─────────────────────────── component ───────────────────────────

export default function EcosystemPathwayMap({
  ecosystem, biology, metabolic, density, conditions: conditionsProp,
  nutrition, lang = "bg",   // iter854 — nutrition = {col3_avg, pral_avg, ...} from BHI snapshot signals
}) {
  const { API, user } = useAuth();
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState(null);        // Feature G — expanded parent id
  const [showConditions, setShowConditions] = useState(true);  // Feature D
  const [fetchedConditions, setFetchedConditions] = useState([]);
  // iter836 — Feature C: What-If simulation state (client-side).
  // Null means "not overridden — use real data". User can toggle sliders open.
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [whatIf, setWhatIf] = useState({ hscrp: null, omega3: null, vit_d: null, homa_ir: null });
  // iter836 — Feature H: mobile compact vertical mode (auto-detect + toggle).
  const [compactMode, setCompactMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });
  useEffect(() => {
    const onResize = () => setCompactMode(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // iter838 — Feature E: Timeline mode + window selector.
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineWindow, setTimelineWindow] = useState(6);  // 3, 6, or 12 months
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [showPulses, setShowPulses] = useState(true);  // iter934 — pathway pulse overlay toggle
  useEffect(() => {
    if (!timelineOpen || !user) return;
    let cancel = false;
    (async () => {
      setTimelineLoading(true);
      try {
        const { data } = await axios.get(`${API}/breast-intelligence/ecosystem/timeline`, {
          params: { window: timelineWindow },
        });
        if (!cancel) setTimelineData(data);
      } catch { if (!cancel) setTimelineData(null); }
      finally { if (!cancel) setTimelineLoading(false); }
    })();
    return () => { cancel = true; };
  }, [API, user, timelineOpen, timelineWindow]);

  // iter934 — Pathway Pulse Overlay: fetches active 24-48h habit-driven
  // boosts from Living Metabolism and overlays a "live pulse" halo on
  // affected nodes. Refetches on `habits-changed` event so tapping a
  // habit in QuickLogHabitsCard visually lights up the map instantly.
  const [pulses, setPulses] = useState({});
  const loadPulses = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/living-metabolism/pathway-pulses`);
      setPulses(data?.nodes || {});
    } catch { setPulses({}); }
  }, [API, user]);
  useEffect(() => { loadPulses(); }, [loadPulses]);
  useHealthEvent("habits-changed", () => { loadPulses(); });
  const pulseNodeCount = Object.keys(pulses).length;

  // Fetch user conditions when not provided by parent.
  const loadConditions = useCallback(async () => {
    if (!user || conditionsProp) return;
    try {
      const { data } = await axios.get(`${API}/health-conditions`);
      setFetchedConditions(data?.items || []);
    } catch { setFetchedConditions([]); }
  }, [API, user, conditionsProp]);
  useEffect(() => { loadConditions(); }, [loadConditions]);
  useHealthEvent("conditions-changed", () => { loadConditions(); });

  const conditions = conditionsProp || fetchedConditions;

  const desc = NODE_DESC[lang] || NODE_DESC.bg;
  const sevLabel = SEV_LABEL[lang] || SEV_LABEL.bg;

  const nodes = ecosystem?.nodes || [];
  const edges = ecosystem?.edges || [];

  const components = metabolic?.components || {};

  // Burden lookup per node — includes what-if simulation overrides (iter836).
  const nodeBurden = useMemo(() => {
    const b = {};
    ["insulin", "igf1", "inflammation", "estrogen", "adiposity"].forEach((k) => {
      const s = components?.[k]?.score;
      b[k] = s != null ? Math.round(100 - s) : null;
    });
    // Bio inputs
    const vitD = whatIf.vit_d != null ? whatIf.vit_d : (biology?.vitamin_d || biology?.vit_d_ng_ml || null);
    if (vitD != null) b.vit_d = vitD < 20 ? 70 : vitD < 30 ? 45 : 15;
    const o3 = whatIf.omega3 != null ? whatIf.omega3 : (biology?.omega3_index || null);
    if (o3 != null) b.omega3 = o3 < 4 ? 70 : o3 < 8 ? 40 : 15;

    // iter836 — Apply What-If overrides on inflammation + insulin axes.
    if (whatIf.hscrp != null) {
      // hsCRP-driven inflammation burden (public NIH thresholds).
      const inflBurden = whatIf.hscrp >= 3 ? 75 : whatIf.hscrp >= 1 ? 45 : 15;
      // Boost with low omega-3 if user is simulating both.
      const omegaBonus = (o3 != null && o3 < 4) ? 10 : 0;
      b.inflammation = Math.min(100, inflBurden + omegaBonus);
    } else if (o3 != null && b.inflammation != null) {
      // Slight modulation from omega-3 slider even without hsCRP override.
      const shift = o3 >= 8 ? -10 : o3 < 4 ? 15 : 0;
      b.inflammation = Math.max(0, Math.min(100, b.inflammation + shift));
    }
    if (whatIf.homa_ir != null) {
      b.insulin = whatIf.homa_ir >= 2.5 ? 75 : whatIf.homa_ir >= 1.9 ? 50 : 15;
    }

    // KEGG pathways inherit from parent metabolic axis
    if (b.inflammation != null) b.nfkb = b.inflammation;
    if (b.insulin != null || b.igf1 != null) {
      b.pi3k_akt = Math.max(b.insulin ?? 0, b.igf1 ?? 0);
    }
    if (b.estrogen != null) b.esr1 = b.estrogen;
    // iter839 — New KEGG pathway inheritance
    if (b.insulin != null) b.age_rage = b.insulin;               // glycation follows insulin/glucose burden
    if (b.inflammation != null && b.gut_barrier == null && biology?.lps_score != null) {
      b.gut_barrier = biology.lps_score;                          // if lab has LPS marker
    }
    // pparg + autophagy are protective — no direct burden inheritance
    b.tissue = density?.density_percent != null ? Math.round(density.density_percent) : null;
    b.tdlu   = b.tissue;
    // mTOR = worst of insulin+igf1 (proxy)
    if (b.insulin != null || b.igf1 != null) {
      b.mtor = Math.max(b.insulin ?? 0, b.igf1 ?? 0);
    }

    // iter854 — Col3 / PRAL direct node coloring from 14-day meal log.
    // Col3 (higher = better) modulates ECM pathway nodes protectively.
    // PRAL (higher = worse) aggravates inflammation-family pathway nodes.
    const col3 = nutrition?.col3_avg;
    const pral = nutrition?.pral_avg;
    if (col3 != null && col3 > 0) {
      // 100 = fully protective (burden=15), 30 = deficit (burden=70)
      const col3Burden = col3 >= 70 ? 15 : col3 >= 50 ? 30 : col3 >= 30 ? 55 : 70;
      // Colour ECM-family nodes if not already dominated by another signal
      ["col1a1", "col3a1", "lox", "mmp9_timp1", "tgf_beta", "ecm"].forEach((k) => {
        if (b[k] == null || b[k] < col3Burden) b[k] = col3Burden;
      });
    }
    if (pral != null && pral > 0) {
      // PRAL 85→85 burden, 65→55, 45→30, 25→15
      const pralBurden = pral >= 75 ? 85 : pral >= 65 ? 55 : pral >= 45 ? 30 : 15;
      // Colour inflammation-family pathways only when PRAL crosses aggravating threshold
      if (pralBurden >= 55) {
        ["nfkb", "tnf_alpha", "il17", "ifn_gamma", "arachidonic_acid"].forEach((k) => {
          if (b[k] == null || b[k] < pralBurden) b[k] = pralBurden;
        });
        // Slight boost to overall inflammation node too
        if (b.inflammation != null) {
          b.inflammation = Math.min(100, Math.max(b.inflammation, pralBurden));
        }
      }
    }

    return b;
  }, [components, biology, density, whatIf, nutrition]);

  // iter836 — Baseline burden (WITHOUT what-if) — used to show baseline→sim diff.
  const baselineBurden = useMemo(() => {
    const b = {};
    ["insulin", "igf1", "inflammation", "estrogen", "adiposity"].forEach((k) => {
      const s = components?.[k]?.score;
      b[k] = s != null ? Math.round(100 - s) : null;
    });
    const vitD = biology?.vitamin_d || biology?.vit_d_ng_ml || null;
    if (vitD != null) b.vit_d = vitD < 20 ? 70 : vitD < 30 ? 45 : 15;
    const o3 = biology?.omega3_index || null;
    if (o3 != null) b.omega3 = o3 < 4 ? 70 : o3 < 8 ? 40 : 15;
    if (b.inflammation != null) b.nfkb = b.inflammation;
    if (b.insulin != null || b.igf1 != null) b.pi3k_akt = Math.max(b.insulin ?? 0, b.igf1 ?? 0);
    if (b.estrogen != null) b.esr1 = b.estrogen;
    b.tissue = density?.density_percent != null ? Math.round(density.density_percent) : null;
    b.tdlu   = b.tissue;
    if (b.insulin != null || b.igf1 != null) b.mtor = Math.max(b.insulin ?? 0, b.igf1 ?? 0);
    return b;
  }, [components, biology, density]);

  const whatIfActive = useMemo(
    () => Object.values(whatIf).some((v) => v != null),
    [whatIf],
  );

  const severity = (val) => {
    if (val == null) return "insufficient_data";
    if (val >= 60) return "red";
    if (val >= 35) return "yellow";
    return "green";
  };

  // Feature D — Conditions overlay lookup
  const conditionsOnNode = useMemo(() => {
    const map = {};
    if (!showConditions || !Array.isArray(conditions)) return map;
    conditions.forEach((c) => {
      const cid = typeof c === "string" ? c : (c?.condition_id || c?.condition || "");
      const cidNorm = String(cid).trim().toLowerCase();
      const label = typeof c === "string" ? c : (c?.condition || cid);
      const targets = CONDITION_TO_NODES[cidNorm] || [];
      targets.forEach((n) => {
        map[n] = map[n] || [];
        map[n].push({ id: cidNorm, label });
      });
    });
    return map;
  }, [conditions, showConditions]);

  // Resolve positions.
  const positions = useMemo(() => {
    const p = {};
    nodes.forEach((n) => {
      const layout = LAYOUT[n.id] || { x: 400, tier: 0 };
      p[n.id] = { x: layout.x, y: TIER_Y[layout.tier], color: n.color, label: n.label, id: n.id, tier: layout.tier };
    });
    return p;
  }, [nodes]);

  const activeNode  = active ? nodes.find((n) => n.id === active) : null;
  const activeDrv   = active ? NODE_DRIVER_SOURCE[active] : null;
  const activeComp  = activeDrv?.comp ? components[activeDrv.comp] : null;
  const activeSubs  = active ? SUB_NODES[active] : null;

  return (
    <div className="w-full" data-testid="ecosystem-pathway-map">
      {/* Controls (Feature D toggle + Feature C what-if + Feature H compact) */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => setShowConditions((v) => !v)}
          data-testid="ecosystem-toggle-conditions"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
            showConditions
              ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          }`}
        >
          {showConditions ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          Твоите диагнози
        </button>
        <button
          type="button"
          onClick={() => setWhatIfOpen((v) => !v)}
          data-testid="ecosystem-toggle-whatif"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
            whatIfOpen || whatIfActive
              ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          }`}
        >
          🎛️ What-If {whatIfActive && <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />}
        </button>
        <button
          type="button"
          onClick={() => setCompactMode((v) => !v)}
          data-testid="ecosystem-toggle-compact"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
            compactMode
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          }`}
          title={compactMode ? "Превключи към SVG изглед" : "Превключи към списък (mobile-friendly)"}
        >
          📱 {compactMode ? "Списък" : "SVG"}
        </button>
        <button
          type="button"
          onClick={() => setTimelineOpen((v) => !v)}
          data-testid="ecosystem-toggle-timeline"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
            timelineOpen
              ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          }`}
        >
          📈 Timeline
        </button>
        {/* iter934 — Pathway Pulse toggle (Living Metabolism overlay) */}
        <button
          type="button"
          onClick={() => setShowPulses((v) => !v)}
          data-testid="ecosystem-toggle-pulses"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
            showPulses
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          }`}
          title="Показва пътищата активирани от твоите днешни навици (24-48ч)"
        >
          <span className="relative flex items-center">
            🌱
            {pulseNodeCount > 0 && showPulses && (
              <span className="absolute -top-0.5 -right-1 w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
            )}
          </span>
          Живи пътища
          {pulseNodeCount > 0 && (
            <span className={`inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[9px] font-black ${
              showPulses ? "bg-emerald-600 text-white" : "bg-slate-400 text-white"
            }`}>{pulseNodeCount}</span>
          )}
        </button>
        <div className="text-[10px] opacity-60 hidden sm:inline">Клик върху възел за детайли · клик за втори път свива</div>
      </div>

      {/* iter836 — Feature C: What-If sliders panel */}
      <AnimatePresence>
        {whatIfOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-3 overflow-hidden"
            data-testid="ecosystem-whatif-panel"
          >
            <div className="p-3 rounded-xl bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-700/40">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="text-[11px] uppercase font-bold tracking-widest text-cyan-700 dark:text-cyan-300">
                    🎛️ Ако...
                  </div>
                  <p className="text-[11px] opacity-70 mt-0.5 leading-snug">
                    Симулирай как би изглеждала картата ако промениш тези биомаркери. Не се записват — само визуализация.
                  </p>
                </div>
                {whatIfActive && (
                  <button
                    type="button"
                    onClick={() => setWhatIf({ hscrp: null, omega3: null, vit_d: null, homa_ir: null })}
                    data-testid="ecosystem-whatif-reset"
                    className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-white dark:bg-slate-900 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100"
                  >
                    Ресет
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {WHATIF_CFG.map((cfg) => {
                  const val = whatIf[cfg.key];
                  const displayVal = val != null ? val : cfg.target;
                  return (
                    <div key={cfg.key} className="p-2 rounded-lg bg-white dark:bg-slate-900/50 border border-cyan-200/60 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[11px] font-bold">{cfg.label}</label>
                        <span className={`text-[11px] font-black tabular-nums ${val != null ? "text-cyan-600 dark:text-cyan-400" : "opacity-50"}`}>
                          {val != null ? displayVal.toFixed(cfg.step < 1 ? 1 : 0) : `→ ${cfg.target}`}{cfg.unit ? ` ${cfg.unit}` : ""}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={cfg.min}
                        max={cfg.max}
                        step={cfg.step}
                        value={displayVal}
                        onChange={(e) => setWhatIf({ ...whatIf, [cfg.key]: parseFloat(e.target.value) })}
                        data-testid={`ecosystem-whatif-${cfg.key}`}
                        className="w-full accent-cyan-500"
                      />
                      <div className="flex items-center justify-between text-[9px] opacity-60 mt-0.5">
                        <span>{cfg.min}</span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold">цел: {cfg.target}</span>
                        <span>{cfg.max}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* iter838 — Feature E: Timeline panel */}
      <AnimatePresence>
        {timelineOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-3 overflow-hidden"
            data-testid="ecosystem-timeline-panel"
          >
            <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-700/40">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase font-bold tracking-widest text-purple-700 dark:text-purple-300">
                    📈 Тенденции на тежестта
                  </div>
                  <p className="text-[11px] opacity-70 mt-0.5 leading-snug">
                    От записаните ти лабораторни snapshot-и през последните {timelineWindow} мес. Sparkline-и под всеки възел с данни.
                  </p>
                </div>
                <div className="flex items-center gap-1" data-testid="ecosystem-timeline-window">
                  {[3, 6, 12].map((w) => (
                    <button
                      key={w}
                      type="button"
                      onClick={() => setTimelineWindow(w)}
                      data-testid={`ecosystem-timeline-window-${w}`}
                      className={`px-2 py-1 rounded-md text-[10px] font-bold transition-colors ${
                        timelineWindow === w
                          ? "bg-purple-600 text-white"
                          : "bg-white dark:bg-slate-900 text-purple-700 dark:text-purple-300 hover:bg-purple-100"
                      }`}
                    >
                      {w} мес.
                    </button>
                  ))}
                </div>
              </div>
              {timelineLoading && (
                <div className="text-[11px] opacity-70 italic">Зареждане на тенденциите…</div>
              )}
              {!timelineLoading && timelineData && (
                <div>
                  {timelineData.count === 0 ? (
                    <div className="text-[11px] italic opacity-70" data-testid="ecosystem-timeline-empty">
                      Няма лабораторни данни в този период. Качете лабораторни резултати (hsCRP, HOMA-IR, витамин D, омега-3), за да се появят тенденциите.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="ecosystem-timeline-grid">
                      {(timelineData.nodes_with_data || []).map((nid) => {
                        const series = timelineData.series[nid] || [];
                        const first = series[0]?.burden;
                        const last  = series[series.length - 1]?.burden;
                        const delta = last != null && first != null ? last - first : null;
                        const nodeMeta = nodes.find((n) => n.id === nid);
                        return (
                          <button
                            key={nid}
                            type="button"
                            onClick={() => setActive(nid)}
                            data-testid={`ecosystem-timeline-node-${nid}`}
                            className="text-left p-2 rounded-lg bg-white dark:bg-slate-900/50 border border-purple-200/60 dark:border-slate-700 hover:border-purple-400 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="text-[11px] font-bold truncate">{nodeMeta?.label || nid}</span>
                              {delta != null && (
                                <span className={`text-[10px] font-black tabular-nums ${delta < 0 ? "text-emerald-600" : delta > 0 ? "text-rose-600" : "opacity-50"}`}>
                                  {delta > 0 ? "+" : ""}{delta}
                                </span>
                              )}
                            </div>
                            <MiniSparkline series={series} width={110} height={26} />
                            <div className="flex items-center justify-between text-[9px] opacity-60 mt-0.5 tabular-nums">
                              <span>{first != null ? first : "—"}</span>
                              <span>→</span>
                              <span className="font-bold" style={{ color: SEV_COLOR[severity(last)] }}>{last != null ? last : "—"}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* iter836 — Feature H: Compact vertical mode for mobile */}
      {compactMode ? (
        <div className="space-y-3" data-testid="ecosystem-compact-view">
          {[0, 0.5, 1, 1.5, 2, 3].map((tier) => {
            const tierNodes = nodes.filter((n) => (LAYOUT[n.id]?.tier ?? -1) === tier);
            if (tierNodes.length === 0) return null;
            return (
              <div key={tier} className="rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 p-3">
                <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500 mb-2">
                  Ниво {tier} · {TIER_LABELS_BG[tier]}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tierNodes.map((n) => {
                    const isActive = active === n.id;
                    const burden = nodeBurden[n.id];
                    const sev = severity(burden);
                    const dyn = burden != null ? SEV_COLOR[sev] : n.color;
                    const condCnt = (conditionsOnNode[n.id] || []).length;
                    const cPulse = showPulses ? pulses[n.id] : null;
                    return (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => setActive(isActive ? null : n.id)}
                        data-testid={`ecosystem-node-${n.id}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] font-bold transition-all ${
                          isActive ? "ring-2 ring-amber-500 scale-105 shadow-lg" : "ring-1 ring-black/10 dark:ring-white/10"
                        } ${cPulse ? (cPulse.polarity === "protective" ? "shadow-[0_0_0_2px_rgba(16,185,129,0.55)]" : "shadow-[0_0_0_2px_rgba(245,158,11,0.55)]") : ""}`}
                        style={{ backgroundColor: dyn, color: "white" }}
                      >
                        <span>{n.label}</span>
                        {burden != null && (
                          <span className="inline-flex items-center justify-center min-w-[22px] h-[16px] px-1 rounded-full bg-white/90 text-[10px] font-black" style={{ color: dyn }}>
                            {burden}
                          </span>
                        )}
                        {cPulse && (
                          <span
                            className={`inline-flex items-center gap-0.5 px-1.5 h-[16px] rounded-full text-[9px] font-black ${
                              cPulse.polarity === "protective" ? "bg-emerald-500" : "bg-amber-500"
                            } text-white animate-pulse`}
                            data-testid={`ecosystem-pulse-badge-${n.id}`}
                            title={`Активиран от навици · остават ${Math.round(cPulse.hours_remaining)}ч`}
                          >
                            {(cPulse.sources?.[0]?.emoji) || "🌱"} {Math.max(1, Math.round(cPulse.hours_remaining))}ч
                          </span>
                        )}
                        {condCnt > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-indigo-600 text-white text-[10px]" data-testid={`ecosystem-conditions-${n.id}`}>
                            🩺
                          </span>
                        )}
                        {n.kegg && (
                          <span
                            role="link"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); window.open(`https://www.kegg.jp/pathway/${n.kegg}`, "_blank", "noopener"); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); window.open(`https://www.kegg.jp/pathway/${n.kegg}`, "_blank", "noopener"); } }}
                            data-testid={`ecosystem-kegg-link-${n.id}`}
                            className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white text-[9px] font-bold hover:scale-110 transition"
                            style={{ color: dyn }}
                          >K</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`} className="w-full h-auto min-w-[900px]" data-testid="ecosystem-svg-view">
          <defs>
            <marker id="arrow-eco" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#9CA3AF" />
            </marker>
            <filter id="nodeGlowV2" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="8" />
            </filter>
          </defs>

          {/* Tier band labels */}
          {Object.entries(TIER_Y).map(([tier, y]) => (
            <g key={tier}>
              <text
                x={20} y={y - 34}
                style={{ fontSize: 11, fontWeight: 700, fill: "#94a3b8", letterSpacing: "0.05em", textTransform: "uppercase" }}
              >
                {TIER_LABELS_BG[tier]}
              </text>
              <line x1={0} y1={y - 42} x2={CANVAS_W} y2={y - 42} stroke="#e2e8f0" strokeDasharray="2 6" opacity={0.4} />
            </g>
          ))}

          {/* Edges */}
          {edges.map((e, i) => {
            const from = positions[e.from];
            const to = positions[e.to];
            if (!from || !to) return null;
            const protective = e.polarity === "protective";
            const toSev = severity(nodeBurden[e.to]);
            const isHot = !protective && (toSev === "red" || toSev === "yellow");
            const baseWidth = 1.2 + (e.strength || 0.5) * 2.4;
            const width = isHot ? baseWidth + (toSev === "red" ? 2 : 1) : baseWidth;
            const flowColor = protective
              ? "#22C55E"
              : (isHot ? SEV_COLOR[toSev] : "#94a3b8");
            return (
              <motion.line
                key={i}
                x1={from.x} y1={from.y + 34}
                x2={to.x}   y2={to.y - 34}
                stroke={flowColor}
                strokeWidth={width}
                strokeDasharray={protective ? "5 5" : (isHot ? "6 4" : undefined)}
                opacity={protective ? 0.55 : (isHot ? 0.75 : 0.35)}
                markerEnd="url(#arrow-eco)"
                initial={{ pathLength: 0 }}
                animate={{
                  pathLength: 1,
                  ...(isHot ? { strokeDashoffset: [0, -20] } : {}),
                }}
                transition={{
                  pathLength: { duration: 1.2, delay: i * 0.02 },
                  ...(isHot ? {
                    strokeDashoffset: { duration: toSev === "red" ? 1.2 : 2.0, repeat: Infinity, ease: "linear" },
                  } : {}),
                }}
              />
            );
          })}

          {/* Nodes */}
          {nodes.map((n, i) => {
            const pos = positions[n.id];
            if (!pos) return null;
            const isActive = active === n.id;
            const radius = n.id === "tissue" ? 56 : n.id === "mtor" ? 54 : pos.tier === 0.5 ? 42 : pos.tier === 1.5 ? 44 : n.optional ? 42 : 48;
            const burden = nodeBurden[n.id];
            const sev = severity(burden);
            const hasBurden = burden != null;
            const dynamicColor = hasBurden ? SEV_COLOR[sev] : n.color;
            const pulse = sev === "red" || sev === "yellow";
            const conditionsHere = conditionsOnNode[n.id] || [];
            const canExpand = SUB_NODES[n.id] != null;
            const isExpanded = expanded === n.id;

            const lab = n.label || "";
            const useWrap = lab.length > 10;
            const words = lab.split(" ");
            const line1 = useWrap ? words.slice(0, Math.ceil(words.length / 2)).join(" ") : lab;
            const line2 = useWrap ? words.slice(Math.ceil(words.length / 2)).join(" ") : "";
            // iter934 — Pulse overlay (Living Metabolism → this node)
            const nodePulse = showPulses ? pulses[n.id] : null;
            const pulseColor = nodePulse
              ? (nodePulse.polarity === "protective" ? "#10b981" : "#f59e0b")
              : null;
            const pulseOpacity = nodePulse ? Math.max(0.25, nodePulse.strength / 200) : 0;

            return (
              <motion.g
                key={n.id}
                onClick={() => setActive(isActive ? null : n.id)}
                style={{ cursor: "pointer" }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.03 * i, type: "spring", stiffness: 220, damping: 18 }}
                data-testid={`ecosystem-node-${n.id}`}
              >
                {(isActive || pulse) && (
                  <motion.circle
                    cx={pos.x} cy={pos.y}
                    r={radius + 12}
                    fill={dynamicColor}
                    opacity={isActive ? 0.35 : 0.22}
                    filter="url(#nodeGlowV2)"
                    animate={pulse ? { r: [radius + 8, radius + 16, radius + 8], opacity: [0.15, 0.32, 0.15] } : undefined}
                    transition={pulse ? { duration: 2.4, repeat: Infinity, ease: "easeInOut" } : undefined}
                  />
                )}
                {/* iter934 — Pathway Pulse Overlay ring (Living Metabolism) */}
                {nodePulse && (
                  <>
                    <motion.circle
                      cx={pos.x} cy={pos.y}
                      initial={{ r: radius + 4, opacity: pulseOpacity + 0.15 }}
                      r={radius + 6}
                      fill="none"
                      stroke={pulseColor}
                      strokeWidth={3}
                      opacity={pulseOpacity + 0.35}
                      filter="url(#nodeGlowV2)"
                      animate={{
                        r: [radius + 4, radius + 18, radius + 4],
                        opacity: [pulseOpacity + 0.15, pulseOpacity + 0.55, pulseOpacity + 0.15],
                      }}
                      transition={{ duration: 2.0, repeat: Infinity, ease: "easeInOut" }}
                      data-testid={`ecosystem-pulse-${n.id}`}
                    />
                    <motion.circle
                      cx={pos.x} cy={pos.y}
                      initial={{ strokeDashoffset: 0 }}
                      r={radius + 2}
                      fill="none"
                      stroke={pulseColor}
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      opacity={0.9}
                      animate={{ strokeDashoffset: [0, -14] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    />
                  </>
                )}
                <circle
                  cx={pos.x} cy={pos.y} r={radius}
                  fill={dynamicColor}
                  opacity={isActive ? 1 : 0.92}
                  stroke="white" strokeWidth="2"
                />
                {useWrap ? (
                  <>
                    <text x={pos.x} y={pos.y - 2} textAnchor="middle"
                      style={{ fontSize: 12, fontWeight: 700, fill: "white", pointerEvents: "none" }}>{line1}</text>
                    <text x={pos.x} y={pos.y + 12} textAnchor="middle"
                      style={{ fontSize: 12, fontWeight: 700, fill: "white", pointerEvents: "none" }}>{line2}</text>
                  </>
                ) : (
                  <text x={pos.x} y={pos.y + 4} textAnchor="middle"
                    style={{ fontSize: 13, fontWeight: 700, fill: "white", pointerEvents: "none" }}>{lab}</text>
                )}

                {/* Burden badge */}
                {hasBurden && (
                  <g pointerEvents="none">
                    <rect
                      x={pos.x + radius - 18} y={pos.y - radius - 8}
                      width={34} height={16} rx={8}
                      fill="white" stroke={dynamicColor} strokeWidth={1.5}
                    />
                    <text
                      x={pos.x + radius - 1} y={pos.y - radius + 4}
                      textAnchor="middle"
                      style={{ fontSize: 10, fontWeight: 700, fill: dynamicColor }}
                    >{burden}</text>
                  </g>
                )}

                {/* Feature D — Diagnoses chip pill */}
                {conditionsHere.length > 0 && (
                  <g pointerEvents="none" data-testid={`ecosystem-conditions-${n.id}`}>
                    <rect
                      x={pos.x - 22} y={pos.y + radius + 4}
                      width={44} height={16} rx={8}
                      fill="#6366f1" opacity={0.9}
                    />
                    <text
                      x={pos.x} y={pos.y + radius + 16}
                      textAnchor="middle"
                      style={{ fontSize: 10, fontWeight: 700, fill: "white" }}
                    >
                      🩺 {conditionsHere.length}
                    </text>
                  </g>
                )}

                {/* Feature G — Expand indicator */}
                {canExpand && (
                  <g pointerEvents="none">
                    <circle
                      cx={pos.x - radius + 6} cy={pos.y - radius + 6}
                      r={7} fill="white" stroke={dynamicColor} strokeWidth={1.5}
                    />
                    <text
                      x={pos.x - radius + 6} y={pos.y - radius + 9}
                      textAnchor="middle"
                      style={{ fontSize: 10, fontWeight: 800, fill: dynamicColor }}
                    >{isExpanded ? "−" : "+"}</text>
                  </g>
                )}

                {/* iter934 — Live habit pulse mini-badge (emoji + remaining hours) */}
                {nodePulse && (
                  <g pointerEvents="none" data-testid={`ecosystem-pulse-badge-${n.id}`}>
                    <rect
                      x={pos.x - radius - 4} y={pos.y + radius - 10}
                      width={44} height={18} rx={9}
                      fill={pulseColor} opacity={0.95}
                    />
                    <text
                      x={pos.x - radius + 18} y={pos.y + radius + 2}
                      textAnchor="middle"
                      style={{ fontSize: 10, fontWeight: 800, fill: "white" }}
                    >
                      {(nodePulse.sources?.[0]?.emoji) || "🌱"} {Math.max(1, Math.round(nodePulse.hours_remaining))}ч
                    </text>
                  </g>
                )}

                {/* KEGG deep-link icon on Tier 1.5 nodes */}
                {n.kegg && (
                  <g
                    onClick={(e) => { e.stopPropagation(); window.open(`https://www.kegg.jp/pathway/${n.kegg}`, "_blank", "noopener"); }}
                    style={{ cursor: "pointer" }}
                    data-testid={`ecosystem-kegg-link-${n.id}`}
                  >
                    <circle cx={pos.x + radius - 4} cy={pos.y + radius - 4} r={8} fill="white" stroke={dynamicColor} strokeWidth={1.5} />
                    <text
                      x={pos.x + radius - 4} y={pos.y + radius - 1}
                      textAnchor="middle"
                      style={{ fontSize: 9, fontWeight: 800, fill: dynamicColor, pointerEvents: "none" }}
                    >K</text>
                  </g>
                )}
              </motion.g>
            );
          })}
        </svg>
      </div>
      )}

      {/* Detail panel (Feature B + G + F) */}
      <AnimatePresence>
        {active && activeNode && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="mt-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700"
            data-testid="ecosystem-active-detail"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <div className="text-base font-extrabold flex items-center gap-2 flex-wrap">
                  <span>{activeNode.label}</span>
                  {nodeBurden[active] != null && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                      style={{
                        backgroundColor: `${SEV_COLOR[severity(nodeBurden[active])]}22`,
                        color: SEV_COLOR[severity(nodeBurden[active])],
                      }}
                    >
                      тежест {nodeBurden[active]} · {sevLabel[severity(nodeBurden[active])]}
                    </span>
                  )}
                  {whatIfActive && nodeBurden[active] != null && (baselineBurden[active] == null || nodeBurden[active] !== baselineBurden[active]) && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300"
                      data-testid="ecosystem-sim-diff"
                      title="Симулирана стойност (SIM) vs текуща (baseline)"
                    >
                      🎛️ baseline {baselineBurden[active] != null ? baselineBurden[active] : "—"} → sim {nodeBurden[active]}
                    </span>
                  )}
                  {activeNode.kegg && (
                    <a
                      href={`https://www.kegg.jp/pathway/${activeNode.kegg}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      KEGG {activeNode.kegg} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
                <p className="text-xs mt-1 opacity-80 leading-relaxed">{desc[active]}</p>
              </div>
            </div>

            {/* Feature B — Why is this red? Personal drivers */}
            {activeComp?.inputs_used && activeComp.inputs_used.length > 0 && (
              <div className="mt-3 p-2.5 rounded-lg bg-white dark:bg-slate-950/40 border border-slate-200 dark:border-slate-700" data-testid="ecosystem-personal-drivers">
                <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500 mb-1.5 flex items-center gap-1">
                  <Stethoscope className="w-3 h-3" /> Твоите данни, които движат тази ос
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {activeComp.inputs_used.map((v, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                      {v}
                    </span>
                  ))}
                </div>
                {activeComp?.inputs_missing_bg?.length > 0 && (
                  <div className="mt-2 text-[10px] opacity-70">
                    <b>Липсват (за по-точен скор):</b>{" "}
                    {activeComp.inputs_missing_bg.join(" · ")}
                  </div>
                )}
              </div>
            )}

            {activeComp && !activeComp.inputs_used?.length && activeComp.inputs_missing_bg?.length > 0 && (
              <div className="mt-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-700/40 text-[11px] leading-relaxed" data-testid="ecosystem-drivers-empty">
                <Info className="inline w-3 h-3 mr-1 -mt-0.5" />
                Няма достатъчно данни. Добавете: {activeComp.inputs_missing_bg.join(" · ")}.
              </div>
            )}

            {/* iter934 — Pathway Pulse sources (Living Metabolism → this node) */}
            {showPulses && pulses[active] && (
              <div
                className={`mt-3 p-2.5 rounded-lg border ${
                  pulses[active].polarity === "protective"
                    ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-700/40"
                    : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-700/40"
                }`}
                data-testid="ecosystem-pulse-detail"
              >
                <div className={`text-[10px] uppercase font-bold tracking-widest mb-1.5 flex items-center gap-1 ${
                  pulses[active].polarity === "protective"
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-amber-700 dark:text-amber-300"
                }`}>
                  🌱 Живият метаболизъм активира този възел
                  <span className={`ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-black ${
                    pulses[active].polarity === "protective" ? "bg-emerald-600 text-white" : "bg-amber-600 text-white"
                  } animate-pulse`}>
                    сила {pulses[active].strength}/100 · остават {Math.max(1, Math.round(pulses[active].hours_remaining))}ч
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(pulses[active].sources || []).map((s, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        s.polarity === "protective"
                          ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-200"
                          : "bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-200"
                      }`}
                    >
                      <span>{s.emoji}</span>
                      <span>{s.label}</span>
                      <span className="opacity-60 text-[10px] tabular-nums">преди {s.hours_ago < 1 ? "<1" : Math.round(s.hours_ago)}ч</span>
                    </span>
                  ))}
                </div>
                <div className="text-[9px] opacity-70 italic mt-1.5 leading-snug">
                  Ефектът избледнява линейно до {pulses[active].duration_h}ч. Отбележете повече навици в „Живият метаболизъм“, за да поддържате пулса.
                </div>
              </div>
            )}

            {/* Feature D — Conditions influencing this node */}
            {conditionsOnNode[active] && conditionsOnNode[active].length > 0 && (
              <div className="mt-3 p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-700/40" data-testid="ecosystem-conditions-detail">
                <div className="text-[10px] uppercase font-bold tracking-widest text-indigo-600 dark:text-indigo-300 mb-1.5">
                  Твои диагнози, повлияващи този възел
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {conditionsOnNode[active].map((c, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-200">
                      {c.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Feature G — Sub-cluster expansion */}
            {activeSubs && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === active ? null : active)}
                  data-testid={`ecosystem-expand-toggle-${active}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-300 hover:opacity-90"
                >
                  {expanded === active ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  {expanded === active ? "Скрий" : "Разгъни"} биомаркерите ({activeSubs.length})
                </button>
                {expanded === active && (
                  <div className="mt-2 grid sm:grid-cols-3 gap-2" data-testid={`ecosystem-subnodes-${active}`}>
                    {activeSubs.map((s) => (
                      <div key={s.id} className="p-2 rounded-lg bg-white dark:bg-slate-950/40 border border-slate-200 dark:border-slate-700">
                        <div className="font-bold text-xs">{s.label}</div>
                        <div className="text-[10px] opacity-70 leading-snug mt-0.5">{s.hint}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* iter838 — Feature E: Detail panel sparkline (when timeline data loaded) */}
            {timelineData?.series?.[active]?.length > 1 && (
              <div className="mt-3 p-2.5 rounded-lg bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-700/40" data-testid="ecosystem-detail-sparkline">
                <div className="text-[10px] uppercase font-bold tracking-widest text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1">
                  📈 Тенденция ({timelineWindow} мес.)
                </div>
                <MiniSparkline series={timelineData.series[active]} width={220} height={44} />
                <div className="text-[10px] opacity-70 mt-1">
                  {timelineData.series[active].length} точки · {timelineData.series[active][0].burden} → {timelineData.series[active][timelineData.series[active].length - 1].burden}
                </div>
              </div>
            )}

            {/* iter839 — PubMed evidence removed per user request */}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] opacity-70">
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-slate-400" /> {lang === "en" ? "drives" : "влияе ↑"}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 border-t border-dashed border-green-500" /> {lang === "en" ? "protective" : "защитен"}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SEV_COLOR.green }} /> {sevLabel.green}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SEV_COLOR.yellow }} /> {sevLabel.yellow}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: SEV_COLOR.red }} /> {sevLabel.red}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white border border-slate-400 text-[8px] font-bold text-slate-600">K</span> KEGG линк
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-indigo-600 text-[8px] font-bold text-white">🩺</span> твоя диагноза
        </span>
        <span className="flex items-center gap-1" data-testid="ecosystem-pulse-legend">
          <span className="relative inline-block w-3 h-3">
            <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
            <span className="absolute inset-0 rounded-full bg-emerald-500" />
          </span>
          навик активира път ({pulseNodeCount} активни)
        </span>
      </div>
    </div>
  );
}

// ─────────────────── MiniSparkline (iter838 — Feature E) ───────────────────

function MiniSparkline({ series, width = 110, height = 26 }) {
  if (!series || series.length < 2) {
    return (
      <div style={{ width, height }} className="flex items-center justify-center text-[9px] opacity-50 italic">
        {series?.length === 1 ? "1 точка" : "—"}
      </div>
    );
  }
  const values = series.map((s) => s.burden);
  const min = 0;
  const max = 100;
  const padX = 4;
  const padY = 3;
  const w = width - padX * 2;
  const h = height - padY * 2;
  const step = w / Math.max(series.length - 1, 1);
  const points = values.map((v, i) => {
    const x = padX + i * step;
    const y = padY + h - ((v - min) / (max - min)) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const lastV = values[values.length - 1];
  const firstV = values[0];
  const trendColor = lastV < firstV ? "#10b981" : lastV > firstV ? "#f43f5e" : "#94a3b8";
  const lastX = padX + (series.length - 1) * step;
  const lastY = padY + h - ((lastV - min) / (max - min)) * h;
  return (
    <svg width={width} height={height} className="mt-0.5">
      {/* Threshold guide at burden=60 (red) */}
      <line x1={padX} y1={padY + h - (60 / 100) * h} x2={padX + w} y2={padY + h - (60 / 100) * h} stroke="#f43f5e" strokeWidth={0.5} strokeDasharray="2 2" opacity={0.3} />
      <line x1={padX} y1={padY + h - (35 / 100) * h} x2={padX + w} y2={padY + h - (35 / 100) * h} stroke="#f59e0b" strokeWidth={0.5} strokeDasharray="2 2" opacity={0.3} />
      <polyline points={points} fill="none" stroke={trendColor} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r={2} fill={trendColor} />
    </svg>
  );
}
